<script setup lang="ts" generic="RawItem">
import { ref, computed, watch } from 'vue'
import { useInfiniteScroll, useDebounceFn } from '@vueuse/core'
import { Check, ChevronsUpDown, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import { Popover, PopoverTrigger, PopoverContent} from '@/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import Spinner from '@/components/ui/spinner/Spinner.vue'

export interface ComboboxItem<TRaw> {
  value: string
  label: string
  raw?: TRaw
}

// Don't let fallthrough attrs (notably `aria-invalid` from a wrapping FormControl)
// land on the renderless Popover root — forward them onto the trigger Button so the
// destructive border renders automatically.
defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  modelValue?: string | null
  selectedValues?: string[]
  multiple?: boolean
  items: ComboboxItem<RawItem>[]
  loading?: boolean
  loadingMore?: boolean
  hasMore?: boolean
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  clearable?: boolean
  buttonClass?: string
  // Filter-lens trigger: a compact button (icon + noun + count), not a field
  // showing its own selection. For surfaces where the selection is displayed
  // elsewhere — the planning board renders it as removable chips — so the
  // control row stays a row of buttons instead of two wide fields.
  compact?: boolean
  // Edit-mode seeding: the label to show for a modelValue whose item hasn't
  // been fetched yet (items load on open). Without it the field shows the
  // placeholder even though a value IS selected.
  fallbackLabel?: string
  // Hands the popover body to the #footer slot: the search box and the option
  // list are not rendered. For a footer that becomes a STEP rather than a row —
  // e.g. the job-type combobox's create-in-place form, which must own the popover
  // while it is open instead of hanging under a list the user is done with.
  hideOptions?: boolean
  // The trigger always shows the placeholder, never the selection — for the
  // "select + list" pattern where the caller renders the picked values as its own
  // stacked list and this control is purely the ADD affordance. Chips crammed
  // into a one-line trigger stop scaling at 2–3 long labels.
  placeholderOnly?: boolean
  // The trigger's SHAPE. `input` (default) is the form field every list and
  // dialog uses. `pill` is for a control standing in prose — a masthead, a
  // header — where a full-width bordered field reads as an unfinished form
  // entry rather than as a fact you may change.
  // A shape is a PROP and not a `buttonClass` override because a shape is
  // several properties at once (height, radius, padding, gap) and a caller
  // layering four overrides has to know this component's base string to know
  // what it is fighting. `buttonClass` would in fact win — `cn()` is
  // tailwind-merge and the caller's classes land last — but "it resolves" is
  // not the same as "it is readable": one string per shape puts the whole
  // shape in one place, where the next shape can be added without reading a
  // caller.
  // `add` is the ADD row of a list the caller draws itself (placeholderOnly):
  // a dashed full-width line led by the caller's icon, flat, with no chevron —
  // it adds to a list, it does not hold a value.
  triggerShape?: 'input' | 'pill' | 'add'
}>(), {
  modelValue: null,
  selectedValues: () => [],
  multiple: false,
  loading: false,
  hasMore: false,
  placeholder: '',
  searchPlaceholder: '',
  emptyText: '',
  disabled: false,
  clearable: true,
  buttonClass: '',
  compact: false,
  fallbackLabel: '',
  hideOptions: false,
  placeholderOnly: false,
  triggerShape: 'input',
})

// One string per shape — the losing utilities are never emitted at all, so the
// rendered class list says what the trigger is instead of what it used to be.
// (`compact` is its own trigger below and does not read this.)
const TRIGGER_SHAPE_CLASS = {
  input: 'w-full min-w-0 h-10 justify-between',
  pill: 'w-full min-w-0 h-8 justify-between gap-1.5 rounded-full px-3 text-sm bg-sunken',
  // `!` because the outline button's relief veil and shadow are custom
  // utilities tailwind-merge does not know to replace.
  add: 'w-full min-w-0 h-10 justify-start gap-2 border-dashed bg-transparent !bg-none px-3 font-medium !shadow-none hover:bg-sunken hover:!bg-none active:!shadow-none',
} as const

const emit = defineEmits<{
  'update:modelValue': [value: string | null]
  'update:selectedValues': [value: string[]]
  'search': [term: string]
  'load-more': []
  'open-change': [open: boolean]
}>()

const { t } = useI18n()

const displayPlaceholder = computed(() => props.placeholder || t('common.selectOption'))
const displaySearchPlaceholder = computed(() => props.searchPlaceholder || t('common.searchPlaceholder'))
const displayEmptyText = computed(() => props.emptyText || t('common.noResults'))

const open = ref(false)
const internalSearchTerm = ref('')
const listRef = ref<HTMLElement | null>(null)

const selectedItem = computed(() => {
  const match = props.items.find(item => item.value === props.modelValue)
  if (match) return match
  // A selected value whose item hasn't loaded yet still labels the field.
  if (props.modelValue && props.fallbackLabel) return { value: props.modelValue, label: props.fallbackLabel }
  return null
})

const isSelected = (val: string) =>
  props.multiple ? props.selectedValues.includes(val) : val === props.modelValue

const selectedItems = computed(() =>
  props.items.filter(item => props.selectedValues.includes(item.value))
)

const hasSelection = computed(() =>
  props.multiple ? props.selectedValues.length > 0 : !!props.modelValue
)

// What the trigger DISPLAYS (placeholderOnly keeps it a pure add-button); the
// clear X and the muted-placeholder tint follow this, not hasSelection.
const showSelection = computed(() => hasSelection.value && !props.placeholderOnly)

// The compact trigger's badge: how many values are picked (single-select shows
// no badge — the button's active tint already says "one thing is picked").
const selectionCount = computed(() => (props.multiple ? props.selectedValues.length : 0))

const debouncedSearch = useDebounceFn((term: string) => {
  emit('search', term)
}, 500)

const onSearchInput = (event: Event) => {
  const term = (event.target as HTMLInputElement).value
  internalSearchTerm.value = term
  debouncedSearch(term)
}

useInfiniteScroll(
  listRef,
  () => {
    if (props.hasMore && !props.loadingMore) {
      emit('load-more')
    }
  },
  { distance: 20 }
)

const onSelect = (val: string) => {
  if (props.multiple) {
    const next = props.selectedValues.includes(val)
      ? props.selectedValues.filter(v => v !== val)
      : [...props.selectedValues, val]
    emit('update:selectedValues', next)
  } else {
    // Re-clicking the selected value toggle-deselects — but only when clearable;
    // non-clearable contexts (person rows) must never blank their selection.
    if (val === props.modelValue) {
      if (props.clearable) emit('update:modelValue', null)
    } else {
      emit('update:modelValue', val)
    }
    open.value = false
  }
}

const onClear = () => {
  if (props.multiple) {
    emit('update:selectedValues', [])
  } else {
    emit('update:modelValue', null)
  }
}

// Handed to the #footer slot so a footer step can dismiss the popover when it's
// done (the create-in-place form, once the type exists and is selected).
const closePopover = () => { open.value = false }

watch(open, (val) => {
  emit('open-change', val)
})

watch(open, (isOpen) => {
  if (!isOpen) {
    internalSearchTerm.value = ''
    emit('search', '')
  }
})
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger v-if="compact" as-child>
      <Button
        v-bind="$attrs"
        variant="outline"
        size="sm"
        role="combobox"
        :aria-expanded="open"
        class="font-normal"
        :class="[{ 'bg-accent': hasSelection }, buttonClass]"
        :disabled="disabled"
      >
        <slot name="icon" />
        {{ displayPlaceholder }}
        <Badge v-if="selectionCount" variant="secondary" class="ml-0.5 h-4 px-1 text-[10px] tabular-nums">
          {{ selectionCount }}
        </Badge>
      </Button>
    </PopoverTrigger>
    <PopoverTrigger v-else as-child>
      <!-- min-w-0: the trigger is itself a flex/grid item, and its content is
           nowrap truncate labels — without it the button's min-content width is
           the untruncated text and it blows its parent's track out sideways
           instead of ellipsizing (multi-select with two long type names). -->
      <Button
        v-bind="$attrs"
        variant="outline"
        role="combobox"
        :aria-expanded="open"
        class="font-normal aria-[invalid=true]:border-destructive"
        :class="[TRIGGER_SHAPE_CLASS[triggerShape], { 'text-muted-foreground': !showSelection }, buttonClass]"
        :disabled="disabled"
      >
        <!-- `truncate` on a flex container does NOT ellipsize nested flex
             children — it just clips them raw. Every level of this chain needs
             min-w-0 so the slot's own truncating span is the one that shrinks
             (with an ellipsis) instead of the text being sheared mid-word. -->
        <span class="flex items-center gap-1.5 min-w-0 overflow-hidden">
          <slot v-if="triggerShape === 'add'" name="icon" />
          <template v-if="showSelection && multiple && selectedItems.length > 0">
            <slot name="trigger" :items="selectedItems">
              <span
                v-for="item in selectedItems.slice(0, 2)"
                :key="item.value"
                class="flex min-w-0 items-center"
              >
                <slot name="selected" :item="item"><span class="truncate">{{ item.label }}</span></slot>
              </span>
              <span v-if="selectedItems.length > 2" class="text-xs bg-muted px-1.5 py-0.5 rounded font-medium shrink-0">
                +{{ selectedItems.length - 2 }}
              </span>
            </slot>
          </template>
          <template v-else-if="showSelection && !multiple && selectedItem">
            <slot name="selected" :item="selectedItem">
              <span class="truncate">{{ selectedItem.label }}</span>
            </slot>
          </template>
          <template v-else>
            <span class="truncate">{{ displayPlaceholder }}</span>
          </template>
        </span>
        <div v-if="triggerShape !== 'add'" class="flex items-center shrink-0">
          <div
            v-if="showSelection && clearable && !disabled"
            class="mr-2 p-1 rounded-sm hover:bg-sunken"
            @click.stop="onClear"
          >
            <X class="h-3 w-3 text-muted-foreground hover:text-foreground" />
          </div>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-50" />
        </div>
      </Button>
    </PopoverTrigger>
    <!-- The list matches the trigger's width (never narrower than a readable
         floor) so long labels get the same room the trigger got. -->
    <PopoverContent class="w-[var(--reka-popover-trigger-width)] min-w-72 p-0" side="bottom" align="start">
      <!-- Optional caller-owned strip above the search (e.g. the planning
           board's kind toggle). Bare slot, no wrapper: a forwarded-but-empty
           slot must render nothing. -->
      <slot name="header" />
      <!-- v-show, not v-if: hideOptions hands the body to the footer step
           TEMPORARILY (the create-in-place form). Unmounting would throw away the
           CommandInput's text, so cancelling the step would land on an emptied
           search box over still-filtered results. -->
      <Command v-show="!hideOptions" :should-filter="false">
        <CommandInput :placeholder="displaySearchPlaceholder" @input="onSearchInput" />

        <div v-if="loading" class="flex items-center justify-center py-10">
          <Spinner class="h-6 w-6" />
        </div>
        <CommandEmpty v-else-if="items.length === 0">
          {{ displayEmptyText }}
        </CommandEmpty>
        <CommandList v-else ref="listRef" class="max-h-64 overflow-y-auto">
          <CommandGroup disable-filter>
            <CommandItem
              v-for="item in items"
              :key="item.value"
              :value="item.label"
              class="cursor-pointer"
              @select="onSelect(item.value)"
            >
              <div class="flex-1 min-w-0 overflow-hidden">
                <slot name="item" :item="item">
                  <span class="block truncate">{{ item.label }}</span>
                </slot>
              </div>
              <Check
                class="mr-2 h-4 w-4 shrink-0"
                :class="isSelected(item.value) ? 'opacity-100' : 'opacity-0'"
              />
            </CommandItem>
          </CommandGroup>

          <div v-if="hasMore" class="py-4 text-center border-t border-muted/50">
            <Spinner class="mx-auto h-4 w-4" />
          </div>
        </CommandList>
      </Command>
      <!-- Caller-owned strip BELOW the list (or, with hideOptions, the whole
           body). Bare slot, no wrapper: a forwarded-but-empty slot renders
           nothing. `searchTerm` is the live input, not the debounced query — an
           affordance keyed to what the user typed must not lag it. -->
      <slot name="footer" :search-term="internalSearchTerm" :close="closePopover" />
    </PopoverContent>
  </Popover>
</template>
